import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Alert,
  Card,
  Stack,
  Divider,
  Grid,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';
import {
  RefreshRounded as RefreshIcon,
  VisibilityRounded as VisibilityIcon,
  MailOutlineRounded as EmailIcon,
  PhoneRounded as PhoneIcon,
  CheckRounded as CheckIcon,
  EmergencyShareRounded as EmergencyIcon,
  NotificationsActiveRounded as NewIcon,
  TaskAltRounded as HandledIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

import customerService from '../services/customerService';
import PageHeader from '../components/ui/PageHeader';
import StatCard from '../components/ui/StatCard';
import StatusChip from '../components/ui/StatusChip';
import SearchField from '../components/ui/SearchField';
import EmptyState from '../components/ui/EmptyState';
import LoadingState from '../components/ui/LoadingState';

const EmergencyRequestsList = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [emergencyRequests, setEmergencyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredRequests, setFilteredRequests] = useState([]);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    new: 0,
    handled: 0,
  });

  // Fetch emergency requests
  useEffect(() => {
    fetchEmergencyRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEmergencyRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      // Fetch all customers
      const customers = await customerService.getCustomers();

      // Extract emergency contacts from all customers
      const emergencyContactsList = [];
      let newRequests = 0;

      customers.forEach((customer) => {
        if (customer.contactHistory && customer.contactHistory.length > 0) {
          const customerEmergencyContacts = customer.contactHistory
            .filter((contact) => contact.emergency)
            .map((contact, index) => ({
              // Index keeps the id unique even if two contacts share a timestamp
              id: `${customer._id}-em-${index}`,
              customerId: customer._id,
              customerName: customer.name,
              customerEmail: customer.email,
              customerPhone: customer.phone,
              date: new Date(contact.date),
              rawDate: contact.date,
              subject: contact.subject,
              message: contact.message,
              handled: contact.handled || false,
              assignedTo: customer.assignedTo?.name || null,
            }));

          newRequests += customerEmergencyContacts.filter((req) => !req.handled).length;
          emergencyContactsList.push(...customerEmergencyContacts);
        }
      });

      // Sort by date descending (newest first)
      emergencyContactsList.sort((a, b) => b.date - a.date);

      setEmergencyRequests(emergencyContactsList);
      setFilteredRequests(emergencyContactsList);

      setStats({
        total: emergencyContactsList.length,
        new: newRequests,
        handled: emergencyContactsList.length - newRequests,
      });
    } catch (err) {
      console.error('Failed to fetch emergency requests:', err);
      setError('Failed to load emergency requests. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filter requests based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredRequests(emergencyRequests);
      return;
    }

    const lowerCaseSearch = searchTerm.toLowerCase();
    const filtered = emergencyRequests.filter(
      (request) =>
        request.customerName?.toLowerCase().includes(lowerCaseSearch) ||
        request.customerEmail?.toLowerCase().includes(lowerCaseSearch) ||
        request.customerPhone?.toLowerCase().includes(lowerCaseSearch) ||
        request.subject?.toLowerCase().includes(lowerCaseSearch) ||
        request.message?.toLowerCase().includes(lowerCaseSearch)
    );

    setFilteredRequests(filtered);
  }, [searchTerm, emergencyRequests]);

  const handleViewCustomer = (customerId) => {
    navigate(`/customers/${customerId}`);
  };

  // Mark a request as handled: optimistic UI update, persisted through the
  // customer record, reverted with an error message if the save fails.
  const [savingId, setSavingId] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const handleMarkAsHandled = async (request) => {
    if (!request || savingId) return;

    setSavingId(request.id);
    setError(null);
    setSuccessMessage(null);

    const applyHandled = (value) => {
      const update = (prev) =>
        prev.map((req) => (req.id === request.id ? { ...req, handled: value } : req));
      setEmergencyRequests(update);
      setFilteredRequests(update);
      setStats((prev) => ({
        ...prev,
        new: prev.new + (value ? -1 : 1),
        handled: prev.handled + (value ? 1 : -1),
      }));
    };

    applyHandled(true);

    try {
      await customerService.markEmergencyHandled(request.customerId, request.rawDate);
      setSuccessMessage(`Marked "${request.subject}" as handled.`);
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err) {
      applyHandled(false);
      setError(
        err.message || 'Failed to save the handled status. Please try again.'
      );
    } finally {
      setSavingId(null);
    }
  };

  // Define columns for data grid
  const columns = [
    {
      field: 'date',
      headerName: 'Date & Time',
      width: 150,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight={600} noWrap>
            {format(params.value, 'MMM dd, yyyy')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {format(params.value, 'h:mm a')}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'customerName',
      headerName: 'Customer',
      flex: 1,
      minWidth: 170,
      renderCell: (params) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {params.value}
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary" noWrap>
            {params.row.customerEmail}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'customerPhone',
      headerName: 'Phone',
      width: 150,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.75} alignItems="center">
          <PhoneIcon sx={{ fontSize: 15, color: 'error.main' }} />
          <Typography variant="body2" fontWeight={500} noWrap>
            {params.value}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'subject',
      headerName: 'Issue',
      flex: 1.2,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {params.value}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {params.row.message}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      renderCell: (params) =>
        params.row.handled ? (
          <StatusChip status="handled" label="Handled" />
        ) : (
          <StatusChip status="new" color="error" label="New" />
        ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 140,
      sortable: false,
      align: 'right',
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="View customer">
            <IconButton size="small" onClick={() => handleViewCustomer(params.row.customerId)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Email customer">
            <IconButton size="small" onClick={() => window.open(`mailto:${params.row.customerEmail}`)}>
              <EmailIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {!params.row.handled && (
            <Tooltip title="Mark as handled">
              <span>
                <IconButton
                  size="small"
                  sx={{ color: 'success.main' }}
                  disabled={savingId === params.row.id}
                  onClick={() => handleMarkAsHandled(params.row)}
                >
                  {savingId === params.row.id ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <CheckIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ];

  // Prepare rows for data grid
  const rows = filteredRequests.map((request) => ({
    ...request,
    id: request.id,
  }));

  if (loading) {
    return <LoadingState label="Loading emergency requests…" />;
  }

  return (
    <Box>
      <PageHeader
        title="Emergency Requests"
        subtitle="Urgent customer requests that need immediate attention"
        actions={
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<RefreshIcon />}
            onClick={fetchEmergencyRequests}
            sx={{ borderColor: 'divider', bgcolor: 'background.paper' }}
          >
            Refresh
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Stats */}
      <Grid container spacing={{ xs: 1.5, md: 2.5 }} sx={{ mb: { xs: 2.5, md: 3.5 } }}>
        <Grid item xs={4}>
          <StatCard label="Total" value={stats.total} icon={<EmergencyIcon />} color="error" />
        </Grid>
        <Grid item xs={4}>
          <StatCard label="New" value={stats.new} icon={<NewIcon />} color="warning" />
        </Grid>
        <Grid item xs={4}>
          <StatCard label="Handled" value={stats.handled} icon={<HandledIcon />} color="success" />
        </Grid>
      </Grid>

      {/* Search */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ mb: 2 }}
      >
        <SearchField
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search requests…"
          sx={{ maxWidth: { sm: 420 } }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {filteredRequests.length} request{filteredRequests.length === 1 ? '' : 's'}
        </Typography>
      </Stack>

      {filteredRequests.length === 0 ? (
        <Card>
          <EmptyState
            icon={<HandledIcon />}
            title={searchTerm ? 'No matching requests' : 'All clear'}
            description={
              searchTerm
                ? 'Try a different search term.'
                : 'No emergency requests right now. New urgent requests will appear here.'
            }
            color="success"
          />
        </Card>
      ) : isMobile ? (
        /* ------------------------- Mobile card list ------------------------- */
        <Stack spacing={1.5}>
          {filteredRequests.map((request) => (
            <Card
              key={request.id}
              sx={{
                p: 2,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 4,
                  bgcolor: request.handled ? 'success.main' : 'error.main',
                },
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Typography variant="caption" color="text.secondary">
                  {format(request.date, 'MMM dd, yyyy · h:mm a')}
                </Typography>
                {request.handled ? (
                  <StatusChip status="handled" label="Handled" />
                ) : (
                  <StatusChip status="new" color="error" label="New" />
                )}
              </Stack>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>
                {request.subject}
              </Typography>
              {request.message && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 0.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {request.message}
                </Typography>
              )}
              <Divider sx={{ my: 1.5 }} />
              <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {request.customerName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {request.customerPhone || request.customerEmail || '—'}
                  </Typography>
                </Box>
                {request.customerPhone && (
                  <Tooltip title="Call">
                    <IconButton
                      component="a"
                      href={`tel:${request.customerPhone}`}
                      aria-label={`Call ${request.customerName}`}
                      sx={{
                        bgcolor: alpha(theme.palette.error.main, 0.1),
                        color: 'error.main',
                      }}
                    >
                      <PhoneIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Email">
                  <IconButton
                    component="a"
                    href={`mailto:${request.customerEmail}`}
                    aria-label={`Email ${request.customerName}`}
                    sx={{
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      color: 'info.main',
                    }}
                  >
                    <EmailIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="View customer">
                  <IconButton
                    onClick={() => handleViewCustomer(request.customerId)}
                    aria-label="View customer"
                    sx={{
                      bgcolor: (t) => alpha(t.palette.text.primary, 0.06),
                    }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              {!request.handled && (
                <Button
                  fullWidth
                  variant="outlined"
                  color="success"
                  size="small"
                  disabled={savingId === request.id}
                  startIcon={
                    savingId === request.id ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <CheckIcon />
                    )
                  }
                  onClick={() => handleMarkAsHandled(request)}
                  sx={{ mt: 1.5 }}
                >
                  {savingId === request.id ? 'Saving…' : 'Mark as handled'}
                </Button>
              )}
            </Card>
          ))}
        </Stack>
      ) : (
        /* ------------------------- Desktop data grid ------------------------ */
        <Card sx={{ overflow: 'hidden' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            autoHeight
            rowHeight={72}
            disableRowSelectionOnClick
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
              sorting: { sortModel: [{ field: 'date', sort: 'desc' }] },
            }}
            pageSizeOptions={[10, 25, 50]}
            getRowClassName={(params) => (!params.row.handled ? 'emergency-row' : '')}
            sx={{
              '& .emergency-row': {
                backgroundColor: alpha(theme.palette.error.main, 0.045),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.error.main, 0.08),
                },
              },
            }}
          />
        </Card>
      )}
    </Box>
  );
};

export default EmergencyRequestsList;
